import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Image as ImageIcon,
  Layers,
  Plus,
  X,
} from 'lucide-react';
import { getCurrencySymbol, shopifyApi } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import {
  PRODUCT_FORM_FIELD_CLASS,
  PRODUCT_FORM_INLINE_BUTTON_CLASS,
  PRODUCT_FORM_LABEL_CLASS,
  PRODUCT_FORM_REQUIRED_CLASS,
  PRODUCT_FORM_TEXTAREA_CLASS,
} from '@/components/commerce-v2/productFormChrome';
import { ImageDropZone } from '@/components/ui/image-drop-zone';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/searchable-select';
import { validateProductImageUrl } from '@/lib/commerce/productImageUrl';
import {
  buildShopifyProductPayload,
  type ShopifyImagePayload,
} from '@/lib/commerce/storeProductPayload';
import {
  SHOPIFY_PRODUCT_STATUSES,
  type ShopifyProductStatus,
} from '@/lib/commerce/productVocabulary';


export interface ShopifyImageEntry {
  file?: File;
  preview: string;
  base64?: string;
  existingId?: number;
}

export interface ShopifyVariant {
  id?: number;
  option1: string;
  sku: string;
  price: string;
  inventory_quantity: number | '';
  image: ShopifyImageEntry | null;
}

export const emptyShopifyVariant = (): ShopifyVariant => ({
  option1: '',
  sku: '',
  price: '',
  inventory_quantity: 0,
  image: null,
});

const IMAGE_FILE_ACCEPT = 'image/' + '*';

export const SHOPIFY_STATUS_CONFIG: Record<
  ShopifyProductStatus,
  { label: string; color: string }
> = {
  active: { label: 'Active', color: 'bg-emerald-500' },
  draft: { label: 'Draft', color: 'bg-slate-400' },
  archived: { label: 'Archived', color: 'bg-orange-400' },
};

export interface ShopifyCollection {
  id: number;
  title: string;
}

export interface ShopifyTaxonomyCategory {
  id: string;
  name: string;
  fullName: string;
  isLeaf: boolean;
}

export interface ShopifyProductForm {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  descriptionIsHtml?: boolean;
  vendor: string;
  setVendor: (v: string) => void;
  customVendor: string;
  setCustomVendor: (v: string) => void;
  productType: string;
  collectionId: string;
  setCollectionId: (v: string) => void;
  creatingCategory: boolean;
  openCreateCategory: () => void;
  cancelCreateCategory: () => void;
  newCategory: string;
  setNewCategory: (v: string) => void;
  savingCategory: boolean;
  createCategory: () => Promise<void>;
  taxonomyCategoryId: string;
  taxonomyCategoryName: string;
  taxonomyCategoryResults: ShopifyTaxonomyCategory[];
  taxonomyCategorySearching: boolean;
  searchTaxonomyCategories: (query: string) => void;
  selectTaxonomyCategory: (category: ShopifyTaxonomyCategory) => void;
  clearTaxonomyCategory: () => void;
  accountId: string | null;
  setProductType: (v: string) => void;
  customProductType: string;
  setCustomProductType: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  tags: string[];
  tagInput: string;
  setTagInput: (v: string) => void;
  addTag: (raw: string) => void;
  removeTag: (index: number) => void;
  onTagKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  variants: ShopifyVariant[];
  addVariant: () => void;
  removeVariant: (index: number) => void;
  updateVariant: <K extends keyof ShopifyVariant>(
    index: number,
    field: K,
    value: ShopifyVariant[K],
  ) => void;
  setVariantImage: (index: number, files: FileList | null) => Promise<void>;
  mainImages: ShopifyImageEntry[];
  mainImageUrls: string[];
  imageUrl: string;
  setImageUrl: (value: string) => void;
  addImageUrl: () => void;
  removeMainImageUrl: (index: number) => void;
  addMainFiles: (files: FileList | null) => Promise<void>;
  removeMainImage: (index: number) => void;
  vendors: string[];
  productTypes: string[];
  collections: ShopifyCollection[];
  smartCollectionCount: number;
  metaLoading: boolean;
  currencySymbol: string;
  loading: boolean;
  loadingMsg: string;
  singleVariant: boolean;
  canSubmit: boolean;
  reset: () => void;
  submit: () => Promise<{ ok: boolean; productId?: string }>;
}

export function useShopifyCategoryField({ accountId }: { accountId: string | null }) {
  const [collectionId, setCollectionId] = React.useState('');
  const [collections, setCollections] = React.useState<ShopifyCollection[]>([]);
  const [smartCollectionCount, setSmartCollectionCount] = React.useState(0);
  const [creatingCategory, setCreatingCategory] = React.useState(false);
  const [newCategory, setNewCategory] = React.useState('');
  const [savingCategory, setSavingCategory] = React.useState(false);

  const openCreateCategory = React.useCallback(() => setCreatingCategory(true), []);

  const cancelCreateCategory = React.useCallback(() => {
    setCreatingCategory(false);
    setNewCategory('');
  }, []);

  const resetCategory = React.useCallback(() => {
    setCollectionId('');
    setCreatingCategory(false);
    setNewCategory('');
  }, []);

  const createCategory = React.useCallback(async () => {
    const clean = newCategory.trim();
    if (!clean) {
      toast({ title: 'Enter a collection name', variant: 'warning' });
      return;
    }

    const known = collections.find(
      (c) => c.title.trim().toLowerCase() === clean.toLowerCase(),
    );
    if (known) {
      setCollectionId(String(known.id));
      setCreatingCategory(false);
      setNewCategory('');
      toast({ title: `"${known.title}" already exists — selected it`, variant: 'warning' });
      return;
    }

    if (!accountId) {
      toast({ title: 'Connect a Shopify store first', variant: 'destructive' });
      return;
    }

    setSavingCategory(true);
    try {
      const created = await shopifyApi.createCollection(accountId, clean);
      setCollections((prev) =>
        prev.some((c) => c.id === created.id)
          ? prev
          : [...prev, { id: created.id, title: created.title }].sort((a, b) =>
              a.title.localeCompare(b.title),
            ),
      );
      setCollectionId(String(created.id));
      setCreatingCategory(false);
      setNewCategory('');
      toast({
        title: created.existing
          ? `"${created.title}" already existed in Shopify — selected it`
          : `Collection "${created.title}" created in Shopify`,
        variant: created.existing ? 'warning' : 'success',
      });
    } catch (e) {
      showApiError(e, { fallback: { title: 'Could not create the category' } });
    } finally {
      setSavingCategory(false);
    }
  }, [accountId, collections, newCategory]);

  return {
    collectionId,
    setCollectionId,
    collections,
    setCollections,
    smartCollectionCount,
    setSmartCollectionCount,
    creatingCategory,
    openCreateCategory,
    cancelCreateCategory,
    newCategory,
    setNewCategory,
    savingCategory,
    createCategory,
    resetCategory,
  };
}

const TAXONOMY_SEARCH_DEBOUNCE_MS = 300;

export function useShopifyTaxonomyCategoryField({
  accountId,
  open,
}: {
  accountId: string | null;
  open: boolean;
}) {
  const [taxonomyCategoryId, setTaxonomyCategoryId] = React.useState('');
  const [taxonomyCategoryName, setTaxonomyCategoryName] = React.useState('');
  const [taxonomyCategoryResults, setTaxonomyCategoryResults] = React.useState<
    ShopifyTaxonomyCategory[]
  >([]);
  const [taxonomyCategorySearching, setTaxonomyCategorySearching] =
    React.useState(false);

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = React.useRef(0);

  const runSearch = React.useCallback(
    (query: string) => {
      if (!accountId) return;
      const thisRequest = ++requestIdRef.current;
      shopifyApi
        .searchTaxonomyCategories(accountId, query)
        .then((categories) => {
          if (thisRequest !== requestIdRef.current) return;
          setTaxonomyCategoryResults(categories);
        })
        .catch(() => {
          if (thisRequest !== requestIdRef.current) return;
          setTaxonomyCategoryResults([]);
        })
        .finally(() => {
          if (thisRequest === requestIdRef.current) {
            setTaxonomyCategorySearching(false);
          }
        });
    },
    [accountId],
  );

  const searchTaxonomyCategories = React.useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!accountId) return;
      setTaxonomyCategorySearching(true);
      debounceRef.current = setTimeout(
        () => runSearch(query),
        TAXONOMY_SEARCH_DEBOUNCE_MS,
      );
    },
    [accountId, runSearch],
  );

  React.useEffect(() => {
    if (!open || !accountId) return;
    runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId]);

  const selectTaxonomyCategory = React.useCallback(
    (category: ShopifyTaxonomyCategory) => {
      setTaxonomyCategoryId(category.id);
      setTaxonomyCategoryName(category.fullName);
    },
    [],
  );

  const clearTaxonomyCategory = React.useCallback(() => {
    setTaxonomyCategoryId('');
    setTaxonomyCategoryName('');
  }, []);

  const resetTaxonomyCategory = React.useCallback(() => {
    clearTaxonomyCategory();
    setTaxonomyCategoryResults([]);
    setTaxonomyCategorySearching(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [clearTaxonomyCategory]);

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return {
    taxonomyCategoryId,
    taxonomyCategoryName,
    taxonomyCategoryResults,
    taxonomyCategorySearching,
    searchTaxonomyCategories,
    selectTaxonomyCategory,
    clearTaxonomyCategory,
    resetTaxonomyCategory,
  };
}

export function useShopifyProductForm({
  open,
  accountId,
  shopCurrency = 'USD',
  singleVariant = false,
}: {
  open: boolean;
  accountId: string | null;
  shopCurrency?: string;
  singleVariant?: boolean;
}): ShopifyProductForm {
  const currencySymbol = getCurrencySymbol(shopCurrency);

  const [loading, setLoading] = React.useState(false);
  const [loadingMsg, setLoadingMsg] = React.useState('Creating…');

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [vendor, setVendor] = React.useState('');
  const [customVendor, setCustomVendor] = React.useState('');
  const [productType, setProductType] = React.useState('');
  const category = useShopifyCategoryField({ accountId });
  const {
    collectionId,
    setCollectionId,
    collections,
    setCollections,
    smartCollectionCount,
    setSmartCollectionCount,
  } = category;
  const taxonomyCategory = useShopifyTaxonomyCategoryField({ accountId, open });
  const {
    taxonomyCategoryId,
    taxonomyCategoryName,
    taxonomyCategoryResults,
    taxonomyCategorySearching,
    searchTaxonomyCategories,
    selectTaxonomyCategory,
    clearTaxonomyCategory,
  } = taxonomyCategory;
  const [customProductType, setCustomProductType] = React.useState('');
  const [status, setStatus] = React.useState('active');
  const [tags, setTags] = React.useState<string[]>([]);
  const [tagInput, setTagInput] = React.useState('');
  const [variants, setVariants] = React.useState<ShopifyVariant[]>([
    emptyShopifyVariant(),
  ]);
  const [mainImages, setMainImages] = React.useState<ShopifyImageEntry[]>([]);
  const [mainImageUrls, setMainImageUrls] = React.useState<string[]>([]);
  const [imageUrl, setImageUrl] = React.useState('');

  const addImageUrl = React.useCallback(() => {
    const raw = imageUrl.trim();
    const problem = validateProductImageUrl(raw);
    if (problem) {
      toast({ title: problem, variant: 'warning' });
      return;
    }
    if (mainImageUrls.includes(raw)) {
      toast({ title: 'That image is already added', variant: 'warning' });
      setImageUrl('');
      return;
    }
    setMainImageUrls((prev) => [...prev, raw]);
    setImageUrl('');
  }, [imageUrl, mainImageUrls]);

  const removeMainImageUrl = React.useCallback((index: number) => {
    setMainImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const [vendors, setVendors] = React.useState<string[]>([]);
  const [productTypes, setProductTypes] = React.useState<string[]>([]);
  const [metaLoading, setMetaLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !accountId) return;
    setMetaLoading(true);
    shopifyApi
      .getStoreMeta(accountId)
      .then((meta) => {
        setVendors(meta.vendors || []);
        setProductTypes(meta.productTypes || []);
        setCollections(meta.collections || []);
        setSmartCollectionCount((meta.smartCollections || []).length);
        if (meta.vendors?.length) {
          setVendor((prev) => prev || meta.vendors[0]);
        }
      })
      .catch(() => {
      })
      .finally(() => setMetaLoading(false));
  }, [open, accountId]);

  const reset = React.useCallback(() => {
    setTitle('');
    setDescription('');
    setVendor('');
    setCustomVendor('');
    category.resetCategory();
    taxonomyCategory.resetTaxonomyCategory();
    setProductType('');
    setCustomProductType('');
    setStatus('active');
    setTags([]);
    setTagInput('');
    setVariants([emptyShopifyVariant()]);
    setMainImages([]);
    setMainImageUrls([]);
    setImageUrl('');
  }, []);

  const addTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t || tags.includes(t)) return;
    setTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags((prev) => prev.slice(0, -1));
    }
  };

  const removeTag = (i: number) =>
    setTags((prev) => prev.filter((_, idx) => idx !== i));

  const readFile = (file: File): Promise<ShopifyImageEntry> =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('Not an image'));
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        reject(new Error(`${file.name} exceeds 20MB`));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const r = reader.result as string;
        resolve({ file, preview: r, base64: r.split(',')[1] });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const addMainFiles = async (files: FileList | null) => {
    if (!files) return;
    const entries = await Promise.all(
      Array.from(files).map((f) =>
        readFile(f).catch((err) => {
          toast({ title: err.message, variant: 'destructive' });
          return null;
        }),
      ),
    );
    setMainImages((prev) => [
      ...prev,
      ...(entries.filter(Boolean) as ShopifyImageEntry[]),
    ]);
  };

  const removeMainImage = (index: number) =>
    setMainImages((prev) => prev.filter((_, idx) => idx !== index));

  const updateVariant = <K extends keyof ShopifyVariant>(
    index: number,
    field: K,
    value: ShopifyVariant[K],
  ) =>
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

  const setVariantImage = async (index: number, files: FileList | null) => {
    if (!files?.[0]) return;
    try {
      updateVariant(index, 'image', await readFile(files[0]));
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Not an image',
        variant: 'destructive',
      });
    }
  };

  const addVariant = () =>
    setVariants((prev) => [...prev, emptyShopifyVariant()]);

  const removeVariant = (i: number) =>
    setVariants((prev) =>
      prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev,
    );

  const hasPrice = variants.some((v) => v.price !== '');

  const submit = async (): Promise<{ ok: boolean; productId?: string }> => {
    if (!accountId) return { ok: false };
    if (!title.trim()) {
      toast({ title: 'Product name is required', variant: 'destructive' });
      return { ok: false };
    }
    if (!hasPrice) {
      toast({
        title: singleVariant
          ? 'Price is required'
          : 'At least one variant needs a price',
        variant: 'destructive',
      });
      return { ok: false };
    }

    setLoading(true);
    setLoadingMsg('Creating product…');
    try {
      const resolvedVendor = vendor === '__custom__' ? customVendor : vendor;
      const resolvedProductType =
        productType === '__custom__' ? customProductType : productType;

      const imagePayload: ShopifyImagePayload[] = [
        ...mainImages
          .filter((img) => img.base64)
          .map((img) => ({ attachment: img.base64 as string })),
        ...mainImageUrls.map((url) => ({ src: url })),
      ];

      const productData = buildShopifyProductPayload({
        title,
        description,
        vendor: resolvedVendor,
        productType: resolvedProductType,
        collectionIds: collectionId ? [Number(collectionId)] : undefined,
        categoryId: taxonomyCategoryId || undefined,
        status,
        tags,
        variants,
        images: imagePayload,
      });

      const shopifyProduct = await shopifyApi.createProduct(
        accountId,
        productData,
      );

      const variantImagesPayload = variants
        .map((v, i) => {
          if (!v.image) return null;
          const cv = shopifyProduct?.variants?.[i];
          if (!cv?.id) return null;
          if (!v.image.base64) return null;
          return { attachment: v.image.base64, variant_ids: [cv.id] };
        })
        .filter(Boolean);

      if (variantImagesPayload.length && shopifyProduct?.id) {
        setLoadingMsg('Uploading variant images…');
        await shopifyApi.updateProduct(accountId, String(shopifyProduct.id), {
          images: variantImagesPayload,
        });
      }

      toast({
        variant: 'success',
        title: 'Product Created',
        description: `"${title}" has been added to Shopify.`,
      });
      const createdId =
        shopifyProduct?.id != null ? String(shopifyProduct.id) : undefined;
      reset();
      return { ok: true, productId: createdId };
    } catch (err) {
      showApiError(err, { fallback: { title: 'Create Failed' } });
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    vendor,
    setVendor,
    customVendor,
    setCustomVendor,
    productType,
    setProductType,
    collectionId,
    setCollectionId,
    creatingCategory: category.creatingCategory,
    openCreateCategory: category.openCreateCategory,
    cancelCreateCategory: category.cancelCreateCategory,
    newCategory: category.newCategory,
    setNewCategory: category.setNewCategory,
    savingCategory: category.savingCategory,
    createCategory: category.createCategory,
    taxonomyCategoryId,
    taxonomyCategoryName,
    taxonomyCategoryResults,
    taxonomyCategorySearching,
    searchTaxonomyCategories,
    selectTaxonomyCategory,
    clearTaxonomyCategory,
    accountId,
    customProductType,
    setCustomProductType,
    status,
    setStatus,
    tags,
    tagInput,
    setTagInput,
    addTag,
    removeTag,
    onTagKeyDown,
    variants,
    addVariant,
    removeVariant,
    updateVariant,
    setVariantImage,
    mainImages,
    mainImageUrls,
    imageUrl,
    setImageUrl,
    addImageUrl,
    removeMainImageUrl,
    addMainFiles,
    removeMainImage,
    vendors,
    productTypes,
    collections,
    smartCollectionCount,
    metaLoading,
    currencySymbol,
    loading,
    loadingMsg,
    singleVariant,
    canSubmit: Boolean(title.trim()) && hasPrice,
    reset,
    submit,
  };
}

export function ShopifyStatusSelect({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn('h-8 text-xs border-slate-200 w-28 font-medium', className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SHOPIFY_PRODUCT_STATUSES.map((val) => (
          <SelectItem key={val} value={val} className="text-xs">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  SHOPIFY_STATUS_CONFIG[val].color,
                )}
              />
              {SHOPIFY_STATUS_CONFIG[val].label}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ShopifyProductFormFields({
  form,
  className,
  statusControl,
  showCollection = true,
}: {
  form: ShopifyProductForm;
  className?: string;
  statusControl?: React.ReactNode;
  showCollection?: boolean;
}) {
  const [dragging, setDragging] = React.useState(false);
  const [vendorBeforeCustom, setVendorBeforeCustom] = React.useState('');
  const [typeBeforeCustom, setTypeBeforeCustom] = React.useState('');
  const fileInputId = React.useId();
  const variantInputId = (index: number) => `${fileInputId}-variant-${index}`;

  return (
    <div className={cn('space-y-6', className)}>
      <section>
        <Label className={cn(PRODUCT_FORM_LABEL_CLASS, 'mb-3 block')}>
          Product Images
        </Label>
        {form.mainImages.length === 0 && form.mainImageUrls.length === 0 ? (
          <>
            <input
              id={fileInputId}
              type="file"
              accept={IMAGE_FILE_ACCEPT}
              multiple
              className="peer sr-only"
              onChange={(e) => form.addMainFiles(e.target.files)}
            />
            <ImageDropZone
              htmlFor={fileInputId}
              dragging={dragging}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                form.addMainFiles(e.dataTransfer.files);
              }}
            />
          </>
        ) : (
          <div
            className={cn(
              'rounded-xl border-2 border-dashed p-3 transition-colors',
              dragging ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              form.addMainFiles(e.dataTransfer.files);
            }}
          >
            <div className="grid grid-cols-5 gap-2">
              {form.mainImages.map((img, i) => (
                <div
                  key={i}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm"
                >
                  <img
                    src={img.preview}
                    alt={img.file?.name ?? ''}
                    className="w-full h-full object-cover"
                  />
                  {i === 0 && (
                    <div className="absolute top-1.5 left-1.5">
                      <Badge className="text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white border-0 hover:bg-black/70">
                        Primary
                      </Badge>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      form.removeMainImage(i);
                    }}
                    className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {form.mainImageUrls.map((url, i) => (
                <div
                  key={url}
                  title={url}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-slate-50"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-1 text-slate-400">
                    <ImageIcon className="h-4 w-4" />
                    <span className="w-full truncate text-center text-[9px] leading-tight">
                      {url.split('/').filter(Boolean)[1] ?? url}
                    </span>
                  </div>
                  <img
                    src={url}
                    alt=""
                    className="relative h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                  {form.mainImages.length === 0 && i === 0 && (
                    <div className="absolute top-1.5 left-1.5">
                      <Badge className="text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white border-0 hover:bg-black/70">
                        Primary
                      </Badge>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      form.removeMainImageUrl(i);
                    }}
                    aria-label={`Remove ${url}`}
                    className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-all"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label
                htmlFor={fileInputId}
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
              >
                <Plus className="h-5 w-5 text-slate-400 group-hover:text-emerald-500" />
                <span className="text-[10px] text-slate-400 group-hover:text-emerald-500 mt-1">
                  Add
                </span>
              </label>
            </div>
            <input
              id={fileInputId}
              type="file"
              accept={IMAGE_FILE_ACCEPT}
              multiple
              className="peer sr-only"
              onChange={(e) => form.addMainFiles(e.target.files)}
            />
          </div>
        )}

        <div className="mt-2 flex gap-2">
          <Input
            value={form.imageUrl}
            onChange={(e) => form.setImageUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                form.addImageUrl();
              }
            }}
            placeholder="https://example.com/image.jpg"
            className={PRODUCT_FORM_FIELD_CLASS}
          />
          <button
            type="button"
            onClick={form.addImageUrl}
            className={PRODUCT_FORM_INLINE_BUTTON_CLASS}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add URL
          </button>
        </div>

      </section>

      <section>
        <div className="space-y-4">
          <div>
            <Label className={PRODUCT_FORM_LABEL_CLASS}>
              Product Name <span className={PRODUCT_FORM_REQUIRED_CLASS}>*</span>
            </Label>
            <Input
              value={form.title}
              onChange={(e) => form.setTitle(e.target.value)}
              placeholder="e.g. Classic Cotton T-Shirt"
              className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
              required
            />
          </div>

          <div>
            <Label className={PRODUCT_FORM_LABEL_CLASS}>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => form.setDescription(e.target.value)}
              rows={3}
              placeholder="Describe your product…"
              className={cn("mt-1.5 resize-none", PRODUCT_FORM_TEXTAREA_CLASS)}
            />
            {form.descriptionIsHtml && (
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                This description has formatting, so the box shows its HTML and
                saves it exactly as written. Editing it as plain text would drop
                the formatting.
              </p>
            )}
          </div>

          <div
            className={cn('grid gap-3', statusControl ? 'grid-cols-3' : 'grid-cols-2')}
          >
            {statusControl && (
              <div>
                <Label className={PRODUCT_FORM_LABEL_CLASS}>Status</Label>
                <div className="mt-1.5 [&>button]:h-[38px] [&>button]:w-full [&>button]:rounded-[8px] [&>button]:border-[#EDF1F7] [&>button]:bg-white [&>button]:text-[13px]">
                  {statusControl}
                </div>
              </div>
            )}
            <div
              className={cn(
                form.vendor === '__custom__' &&
                  (statusControl ? 'col-span-3' : 'col-span-2'),
              )}
            >
              <Label className={PRODUCT_FORM_LABEL_CLASS}>Vendor</Label>
              {form.vendors.length > 0 ? (
                <Select
                  value={form.vendor}
                  onValueChange={(v) => {
                    if (v === '__custom__') setVendorBeforeCustom(form.vendor);
                    form.setVendor(v);
                  }}
                >
                  <SelectTrigger className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}>
                    <SelectValue
                      placeholder={form.metaLoading ? 'Loading…' : 'Select vendor'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {form.vendor &&
                      form.vendor !== '__custom__' &&
                      !form.vendors.includes(form.vendor) && (
                        <SelectItem value={form.vendor}>{form.vendor}</SelectItem>
                      )}
                    {form.vendors.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.vendor}
                  onChange={(e) => form.setVendor(e.target.value)}
                  placeholder={form.metaLoading ? 'Loading…' : 'Brand name'}
                  className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                />
              )}
              {form.vendor === '__custom__' && (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    autoFocus
                    value={form.customVendor}
                    onChange={(e) => form.setCustomVendor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!form.customVendor.trim()) return;
                        form.setVendor(form.customVendor.trim());
                        form.setCustomVendor('');
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        form.setVendor(vendorBeforeCustom);
                        form.setCustomVendor('');
                      }
                    }}
                    placeholder="Enter vendor"
                    className={cn('flex-1', PRODUCT_FORM_FIELD_CLASS)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!form.customVendor.trim()) return;
                      form.setVendor(form.customVendor.trim());
                      form.setCustomVendor('');
                    }}
                    disabled={!form.customVendor.trim()}
                    className={cn(
                      PRODUCT_FORM_INLINE_BUTTON_CLASS,
                      'disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      form.setVendor(vendorBeforeCustom);
                      form.setCustomVendor('');
                    }}
                    className={PRODUCT_FORM_INLINE_BUTTON_CLASS}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div
              className={cn(
                form.productType === '__custom__' &&
                  (statusControl ? 'col-span-3' : 'col-span-2'),
              )}
            >
              <Label className={PRODUCT_FORM_LABEL_CLASS}>
                Product Type
              </Label>
              {form.productTypes.length > 0 ? (
                <Select
                  value={form.productType || '__none__'}
                  onValueChange={(v) => {
                    if (v === '__custom__') setTypeBeforeCustom(form.productType);
                    form.setProductType(v === '__none__' ? '' : v);
                  }}
                >
                  <SelectTrigger className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {form.productType &&
                      form.productType !== '__custom__' &&
                      !form.productTypes.includes(form.productType) && (
                        <SelectItem value={form.productType}>
                          {form.productType}
                        </SelectItem>
                      )}
                    {form.productTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">+ Enter custom…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.productType}
                  onChange={(e) => form.setProductType(e.target.value)}
                  placeholder={form.metaLoading ? 'Loading…' : 'e.g. Clothing'}
                  className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                />
              )}
              {form.productType === '__custom__' && (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    autoFocus
                    value={form.customProductType}
                    onChange={(e) => form.setCustomProductType(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!form.customProductType.trim()) return;
                        form.setProductType(form.customProductType.trim());
                        form.setCustomProductType('');
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        form.setProductType(typeBeforeCustom);
                        form.setCustomProductType('');
                      }
                    }}
                    placeholder="Enter product type"
                    className={cn('flex-1', PRODUCT_FORM_FIELD_CLASS)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!form.customProductType.trim()) return;
                      form.setProductType(form.customProductType.trim());
                      form.setCustomProductType('');
                    }}
                    disabled={!form.customProductType.trim()}
                    className={cn(
                      PRODUCT_FORM_INLINE_BUTTON_CLASS,
                      'disabled:cursor-not-allowed disabled:opacity-60',
                    )}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      form.setProductType(typeBeforeCustom);
                      form.setCustomProductType('');
                    }}
                    className={PRODUCT_FORM_INLINE_BUTTON_CLASS}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className={PRODUCT_FORM_LABEL_CLASS}>Category</Label>
            <div className="mt-1.5">
              <SearchableSelect
                options={form.taxonomyCategoryResults.map(
                  (c): SearchableSelectOption => ({
                    value: c.id,
                    label: c.fullName,
                    meta: c.isLeaf ? undefined : 'Has subcategories',
                  }),
                )}
                pinnedOptions={[{ value: '', label: 'None' }]}
                value={form.taxonomyCategoryId}
                onValueChange={(v, option) => {
                  if (!v) {
                    form.clearTaxonomyCategory();
                    return;
                  }
                  const match = form.taxonomyCategoryResults.find(
                    (c) => c.id === v,
                  );
                  form.selectTaxonomyCategory(
                    match || {
                      id: v,
                      name: option.label,
                      fullName: option.label,
                      isLeaf: true,
                    },
                  );
                }}
                onSearchChange={form.searchTaxonomyCategories}
                loading={form.taxonomyCategorySearching}
                placeholder="Select category"
                searchPlaceholder="Search Shopify's categories…"
                emptyMessage={(query) =>
                  query
                    ? `No category matches "${query}"`
                    : 'Start typing to search'
                }
                disabled={!form.accountId}
              />
            </div>
            {form.taxonomyCategoryId && form.taxonomyCategoryName && (
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                Selected: {form.taxonomyCategoryName}
              </p>
            )}
          </div>

          {showCollection && (
          <div>
            <Label className={PRODUCT_FORM_LABEL_CLASS}>Collection</Label>
            <Select
              value={form.collectionId || '__none__'}
              onValueChange={(v) => {
                if (v === '__new__') {
                  form.openCreateCategory();
                  return;
                }
                form.cancelCreateCategory();
                form.setCollectionId(v === '__none__' ? '' : v);
              }}
              disabled={form.metaLoading}
            >
              <SelectTrigger className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}>
                <SelectValue
                  placeholder={
                    form.metaLoading
                      ? 'Loading…'
                      : form.collections.length === 0
                        ? 'No collections in this store yet'
                        : 'Select collection'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {form.collectionId &&
                  !form.collections.some((c) => String(c.id) === form.collectionId) && (
                    <SelectItem value={form.collectionId}>
                      {form.metaLoading ? 'Current collection…' : `Collection #${form.collectionId}`}
                    </SelectItem>
                  )}
                {form.collections.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.title}
                  </SelectItem>
                ))}
                <SelectItem value="__new__">+ New collection…</SelectItem>
              </SelectContent>
            </Select>

            {form.creatingCategory && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  autoFocus
                  value={form.newCategory}
                  onChange={(e) => form.setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void form.createCategory();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      form.cancelCreateCategory();
                    }
                  }}
                  placeholder="New collection name"
                  disabled={form.savingCategory}
                  className={cn('flex-1', PRODUCT_FORM_FIELD_CLASS)}
                />
                <button
                  type="button"
                  onClick={() => void form.createCategory()}
                  disabled={form.savingCategory || !form.newCategory.trim()}
                  className={cn(
                    PRODUCT_FORM_INLINE_BUTTON_CLASS,
                    'disabled:cursor-not-allowed disabled:opacity-60',
                  )}
                >
                  {form.savingCategory ? 'Creating…' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={form.cancelCreateCategory}
                  disabled={form.savingCategory}
                  className="shrink-0 px-1 text-[13px] font-medium text-slate-500 transition-colors hover:text-slate-700 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            )}
            {!form.metaLoading && form.smartCollectionCount > 0 && (
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                {form.smartCollectionCount} automated{' '}
                {form.smartCollectionCount === 1 ? 'collection' : 'collections'}{' '}
                in this store {form.smartCollectionCount === 1 ? 'is' : 'are'}{' '}
                assigned by Shopify's own rules, so {form.smartCollectionCount === 1 ? 'it is' : 'they are'} not listed here.
              </p>
            )}
          </div>
          )}

          {form.singleVariant && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className={PRODUCT_FORM_LABEL_CLASS}>SKU</Label>
                <Input
                  value={form.variants[0].sku}
                  onChange={(e) => form.updateVariant(0, 'sku', e.target.value)}
                  placeholder="WN-ABC-01"
                  className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                />
              </div>
              <div>
                <Label className={PRODUCT_FORM_LABEL_CLASS}>
                  Price ({form.currencySymbol}){' '}
                  <span className={PRODUCT_FORM_REQUIRED_CLASS}>*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.variants[0].price}
                  onChange={(e) => form.updateVariant(0, 'price', e.target.value)}
                  placeholder="0.00"
                  className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                />
              </div>
              <div>
                <Label className={PRODUCT_FORM_LABEL_CLASS}>
                  Stock
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.variants[0].inventory_quantity}
                  onChange={(e) =>
                    form.updateVariant(
                      0,
                      'inventory_quantity',
                      e.target.value === '' ? '' : parseInt(e.target.value, 10),
                    )
                  }
                  placeholder="0"
                  className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                />
              </div>
            </div>
          )}

          {form.singleVariant && form.variants.length > 1 && (
            <p className="text-[11px] leading-relaxed text-slate-500">
              This product has {form.variants.length} variants. The fields above
              edit the first one; the others keep their current price, SKU and
              stock when you save. Add or change the rest in your Shopify admin.
            </p>
          )}

          <div>
            <Label className={cn(PRODUCT_FORM_LABEL_CLASS, "flex items-center gap-1.5")}>
              Tags
              <span className="font-normal text-slate-400">
                — press Enter or comma to add
              </span>
            </Label>
            <TagChipInput form={form} />
          </div>
        </div>
      </section>

      {!form.singleVariant && (
        <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-800">Variants</h3>
            <Badge variant="outline" className="text-xs">
              {form.variants.length}
            </Badge>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={form.addVariant}
            className="h-8 text-xs border-slate-200"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Variant
          </Button>
        </div>

        <div className="space-y-3">
          {form.variants.map((variant, index) => (
            <div
              key={index}
              className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm"
            >
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className={PRODUCT_FORM_LABEL_CLASS}>
                    {variant.option1 || `Variant ${index + 1}`}
                  </span>
                  {variant.price && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {form.currencySymbol}
                      {variant.price}
                    </Badge>
                  )}
                </div>
                {form.variants.length > 1 && (
                  <button
                    type="button"
                    onClick={() => form.removeVariant(index)}
                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-center gap-4">
                  <div className="shrink-0">
                    {variant.image ? (
                      <div className="relative group w-16 h-16 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                        <img
                          src={variant.image.preview}
                          alt="Variant"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => form.updateVariant(index, 'image', null)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor={variantInputId(index)}
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
                      >
                        <ImageIcon className="h-5 w-5 text-slate-300 group-hover:text-emerald-400" />
                        <span className="text-[9px] text-slate-400 group-hover:text-emerald-500 mt-0.5">
                          Image
                        </span>
                      </label>
                    )}
                    <input
                      id={variantInputId(index)}
                      type="file"
                      accept={IMAGE_FILE_ACCEPT}
                      className="peer sr-only"
                      onChange={(e) => form.setVariantImage(index, e.target.files)}
                    />
                    {variant.image && (
                      <label
                        htmlFor={variantInputId(index)}
                        className="text-[10px] text-emerald-600 hover:underline mt-1 block text-center w-16 cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
                      >
                        Change
                      </label>
                    )}
                  </div>

                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <div>
                      <Label className={PRODUCT_FORM_LABEL_CLASS}>
                        Option
                      </Label>
                      <Input
                        value={variant.option1}
                        onChange={(e) =>
                          form.updateVariant(index, 'option1', e.target.value)
                        }
                        placeholder="S / Red / 1kg"
                        className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                      />
                    </div>
                    <div>
                      <Label className={PRODUCT_FORM_LABEL_CLASS}>
                        SKU
                      </Label>
                      <Input
                        value={variant.sku}
                        onChange={(e) =>
                          form.updateVariant(index, 'sku', e.target.value)
                        }
                        placeholder="WN-ABC-01"
                        className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                      />
                    </div>
                    <div>
                      <Label className={PRODUCT_FORM_LABEL_CLASS}>
                        Price <span className={PRODUCT_FORM_REQUIRED_CLASS}>*</span>
                      </Label>
                      <div className="relative mt-1.5">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-slate-400">
                          {form.currencySymbol}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={variant.price}
                          onChange={(e) =>
                            form.updateVariant(index, 'price', e.target.value)
                          }
                          placeholder="0.00"
                          className={cn(PRODUCT_FORM_FIELD_CLASS, "pl-7")}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className={PRODUCT_FORM_LABEL_CLASS}>
                        Stock
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={variant.inventory_quantity}
                        onChange={(e) =>
                          form.updateVariant(
                            index,
                            'inventory_quantity',
                            e.target.value === ''
                              ? ''
                              : parseInt(e.target.value, 10),
                          )
                        }
                        placeholder="0"
                        className={cn("mt-1.5", PRODUCT_FORM_FIELD_CLASS)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}

export function shopifyProductFormSummary(form: ShopifyProductForm): string {
  const parts: string[] = [];
  if (form.mainImages.length > 0) {
    parts.push(
      `${form.mainImages.length} image${form.mainImages.length > 1 ? 's' : ''}`,
    );
  }
  if (!form.singleVariant) {
    parts.push(
      `${form.variants.length} variant${form.variants.length > 1 ? 's' : ''}`,
    );
  }
  if (form.tags.length > 0) {
    parts.push(`${form.tags.length} tag${form.tags.length > 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
}

function TagChipInput({ form }: { form: ShopifyProductForm }) {
  const tagInputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div
      className="mt-1 min-h-[38px] flex flex-wrap gap-1.5 items-center px-3 py-2 rounded-md border border-input bg-white cursor-text"
      onClick={() => tagInputRef.current?.focus()}
    >
      {form.tags.map((t, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs px-2 py-0.5"
        >
          {t}
          <button
            type="button"
            onClick={() => form.removeTag(i)}
            className="hover:text-red-500 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={tagInputRef}
        value={form.tagInput}
        onChange={(e) => form.setTagInput(e.target.value)}
        onKeyDown={form.onTagKeyDown}
        onBlur={() => form.tagInput.trim() && form.addTag(form.tagInput)}
        placeholder={form.tags.length === 0 ? 'summer, sale, new-arrival…' : ''}
        className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
      />
    </div>
  );
}


